import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ClientNotificationHandlerService, CommonUtilityLib, TargetTypes, TeamTypes } from '@thehubz/client/core';
import { Collection, CollectionsApiService, NotifyOptions, UpdateCollectionDto, UserInfo, UsersTaggingApiService } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { Subscription, filter } from 'rxjs';
import { AssetCommonService } from '../../../../services/asset-common.service';
import { CollectionEventsService } from '../../services/collection-events.service';

@Component({
  selector: 'thehubz-custom-client-metadata',
  templateUrl: './custom-client-metadata.component.html',
  styleUrls: ['./custom-client-metadata.component.scss'],
})
export class CustomClientMetadataComponent implements OnInit, OnDestroy {
  editFlag = false;
  // Custom fields are of type 'any'
  collectionCustomFields!: AnyType;
  collectionId!: string;
  subscriptions = new Subscription();
  collectionMetadataForm: FormGroup = new FormGroup([]);
  campaignYear!: { id: number; name: number }[];
  taggedUser!: UserInfo[]; // tagged user list
  descriptionText!: string; // sp-editor description text

  teamTypes = TeamTypes;
  targetTypes = TargetTypes;

  constructor(
    private fb: FormBuilder,
    private commonUtilityLib: CommonUtilityLib,
    protected assetCommonService: AssetCommonService,
    protected collectionAPIService: CollectionsApiService,
    private usersTaggingApiService: UsersTaggingApiService,
    private clientNotificationService: ClientNotificationHandlerService,
    private collectionEventService: CollectionEventsService,
  ) {}

  ngOnInit(): void {
    const startYear = 2020;
    this.campaignYear = this.assetCommonService.numberRange(startYear, 11);
    // get collection info
    const collectDataSub = this.collectionEventService.collectionDataSubject$
      .pipe(filter((coll) => !this.commonUtilityLib.isEmptyObject(coll)))
      .subscribe((data) => {
        this.collectionCustomFields = data.customFields;
        this.collectionId = data._id;
      });
    // add the collectDataSub Subscription to subscriptions, to be later unsubscribe
    this.subscriptions.add(collectDataSub);
    // form group
    this.collectionMetadataForm = this.fb.group({
      team: ['', [Validators.required]],
      targetGroup: ['', [Validators.required]],
      agency: '',
      campaignYear: '',
      contactPerson: '',
      internalComments: '',
    });
  }

  /**
   * store tagged user list when user tagged get at sp-editor
   * @param userList
   */
  taggedUserList(userList: UserInfo[]) {
    this.taggedUser = userList;
  }

  /**
   * store description text from sp-editor change
   * @param descriptionText
   */
  editorDescriptionText(descriptionText: string) {
    this.descriptionText = descriptionText;
  }

  /**
   * save the updated collection
   */
  saveCollectionInfo() {
    this.editFlag = !this.editFlag;
    const updatedObj: UpdateCollectionDto = this.collectionMetadataForm.getRawValue();
    this.collectionAPIService.collectionsControllerUpdate(this.collectionId, { customFields: updatedObj }).subscribe((collection: Collection) => {
      // notify users tagged in sp-editor component
      if (this.taggedUser && this.taggedUser.length) {
        const notifyOptions: NotifyOptions = {
          userInfo: this.taggedUser,
          callFromContext: '',
          contextPath: 'collection',
          contextId: collection._id,
          contextName: collection.name,
          editorDescription: this.descriptionText,
        };
        this.usersTaggingApiService.usersTaggingControllerNotifyTaggedUsers(notifyOptions).subscribe();
      }
      this.collectionCustomFields = collection.customFields;
      this.clientNotificationService.successNotification($localize`Collection metadata updated`);
    });
  }

  /**
   * edit mode
   */
  editInformation() {
    this.editFlag = !this.editFlag;
    this.patchForm();
  }

  /**
   * patch form
   */
  patchForm() {
    this.collectionMetadataForm.patchValue({
      team: this.collectionCustomFields.team ? this.collectionCustomFields.team : '',
      targetGroup: this.collectionCustomFields.targetGroup ? this.collectionCustomFields.targetGroup : '',
      agency: this.collectionCustomFields.agency ? this.collectionCustomFields.agency : '',
      campaignYear: this.collectionCustomFields.campaignYear ? this.collectionCustomFields.campaignYear : '',
      contactPerson: this.collectionCustomFields.contactPerson ? this.collectionCustomFields.contactPerson : '',
      internalComments: this.collectionCustomFields.internalComments ? this.collectionCustomFields.internalComments : '',
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
